/**
 * Easy Apply modal — TRITAL Care® Careers.
 *
 * Collects a structured, lean application (name, email, LinkedIn, phone,
 * 3 scoped questions) and POSTs to /api/leads with role_interest filled.
 *
 * Why build our own vs. LinkedIn Easy Apply? LinkedIn's Easy Apply requires
 * a paid LinkedIn Recruiter seat and OAuth partner status. Until TRITAL Care
 * ships that, this modal gives candidates the same 30-second UX with full
 * data sovereignty (leads go straight to our MongoDB, not LinkedIn's).
 *
 * Candidates who prefer LinkedIn can click "Continue on LinkedIn" which
 * opens the TRITAL Care company page in a new tab — they can DM from there.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Linkedin, Send, CheckCircle2, ExternalLink, Sparkles, Briefcase, MapPin, Upload, FileText, X, MessageSquare } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import SMSConsentBlock from "./SMSConsentBlock";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINKEDIN_COMPANY_URL = "https://www.linkedin.com/company/tritalcare/";

const YEARS_OPTIONS = ["< 1 year", "1–2 years", "3–5 years", "6–10 years", "10+ years"];
const START_OPTIONS = ["Immediately", "2 weeks", "1 month", "2 months", "Exploring"];
const MAX_RESUME_BYTES = 5 * 1024 * 1024;
const ALLOWED_RESUME_EXTS = [".pdf", ".doc", ".docx"];

export default function EasyApplyModal({ open, onOpenChange, role }) {
  const [form, setForm] = useState({
    name: "", email: "", linkedin: "", phone: "",
    years: YEARS_OPTIONS[2], start: START_OPTIONS[1], why: "",
    smsOptIn: false,
  });
  const [resume, setResume] = useState(null);      // { id, url, filename, size, content_type }
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const firstInput = useRef(null);
  const fileInput = useRef(null);

  // Reset state whenever a different role is selected.
  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setSubmitting(false);
    setResume(null);
    setUploading(false);
    // autofocus first input after the dialog opens
    setTimeout(() => firstInput.current?.focus(), 60);
  }, [open, role?.t]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickResume = () => fileInput.current?.click();
  const removeResume = () => {
    setResume(null);
    if (fileInput.current) fileInput.current.value = "";
  };
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_RESUME_EXTS.includes(ext)) {
      toast.error("Résumé must be PDF, DOC or DOCX");
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      toast.error("Résumé exceeds 5 MB limit");
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    setUploading(true);
    try {
      // Resume binary upload goes to the FastAPI backend if available; on Vercel
      // production this gracefully no-ops and the lead is still captured. CEIPAL
      // remains the canonical resume intake path for production.
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/uploads/resume`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload unavailable");
      const data = await res.json();
      setResume(data);
      toast.success("Résumé uploaded", { description: file.name });
    } catch {
      // Soft-fail: the candidate can still submit the form; we'll just
      // capture the file name in notes so a recruiter can request the file
      // by email after the application lands.
      setResume({ filename: file.name, size: file.size, url: null });
      toast.success("Résumé attached to your application", {
        description: `${file.name} — a recruiter will email you for the file if needed.`,
      });
    } finally {
      setUploading(false);
    }
  };

  const notesBlob = useMemo(() => ([
    role?.t ? `Role: ${role.t}` : null,
    role?.l ? `Location: ${role.l}` : null,
    role?.team ? `Team: ${role.team}` : null,
    form.linkedin ? `LinkedIn: ${form.linkedin.trim()}` : null,
    form.phone ? `Phone: ${form.phone.trim()}` : null,
    `Years in role: ${form.years}`,
    `Earliest start: ${form.start}`,
    form.why ? `Why TRITAL Care: ${form.why.trim()}` : null,
    resume?.url
      ? `Résumé: ${resume.filename} (${(resume.size/1024).toFixed(0)} KB) — ${process.env.REACT_APP_BACKEND_URL || ""}${resume.url}`
      : resume?.filename ? `Résumé attached: ${resume.filename} (${(resume.size/1024).toFixed(0)} KB) — recruiter to follow up via email` : null,
  ].filter(Boolean).join("\n")), [role, form, resume]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "easy-apply",
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          sms_opt_in: form.smsOptIn || false,
          assignmentType: role?.t || "Careers — Easy Apply",
          desiredLocation: role?.l || null,
          notes: notesBlob,
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error("Submission failed");
      setSubmitted(true);
      toast.success("Application received.", {
        description: "We review every application within 5 business days.",
      });
    } catch (err) {
      toast.error("Couldn't send your application", {
        description: err?.message || "Try again in a moment, or email careers@tritalcare.com directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const linkedInCompose = useMemo(() => {
    const subject = `Application · ${role?.t || "Careers"} · TRITAL Care`;
    const body = [
      `Hi TRITAL Care careers team,`,
      ``,
      `I'd like to apply for ${role?.t || "an open role"}${role?.l ? ` (${role.l})` : ""}.`,
      ``,
      form.years ? `Years in role: ${form.years}` : null,
      form.start ? `Earliest start: ${form.start}` : null,
      form.why ? `Why TRITAL Care: ${form.why.trim()}` : null,
      ``,
      `Best,`,
      form.name || `[your name]`,
      form.email || `[your email]`,
    ].filter(Boolean).join("\n");
    return `mailto:careers@tritalcare.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [role, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] p-0 overflow-hidden bg-white rounded-3xl border border-slate-200 shadow-navy" data-testid="easy-apply-modal">
        {/* Header band */}
        <div className="relative bg-trital-navy-ink text-white p-6 lg:p-7 overflow-hidden">
          <div className="absolute inset-0 bg-trital-navy-mesh opacity-80 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15">
              <Sparkles className="w-3 h-3 text-trital-red" strokeWidth={1.8} />
              <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-white/90">Easy Apply · 30 seconds</span>
            </div>
            <DialogHeader className="mt-3 text-left">
              <DialogTitle className="font-display font-black text-[22px] text-white leading-tight" data-testid="easy-apply-title">
                {role?.t || "Apply to TRITAL Care"}
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-white/70 text-[12.5px] flex items-center gap-2 flex-wrap">
                {role?.tag && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider">
                    <Briefcase className="w-3 h-3" strokeWidth={1.8}/> {role.tag}
                  </span>
                )}
                {role?.l && (
                  <span className="inline-flex items-center gap-1 text-white/80">
                    <MapPin className="w-3 h-3" strokeWidth={1.8}/> {role.l}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Body */}
        {submitted ? (
          <div className="p-6 lg:p-7" data-testid="easy-apply-success">
            <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" strokeWidth={1.8} />
            </div>
            <div className="mt-4 font-display font-black text-[22px] text-slate-900 leading-tight">Application received.</div>
            <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
              Thanks for applying to <span className="font-semibold text-slate-900">{role?.t || "TRITAL Care"}</span>. A recruiter will review your application and respond within <span className="font-semibold text-slate-900">5 business days</span> — even if we can't hire you today.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <a
                href={LINKEDIN_COMPANY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#0A66C2] text-white text-[13px] font-semibold hover:bg-[#0952a0]"
                data-testid="easy-apply-success-linkedin"
              >
                <Linkedin className="w-4 h-4" strokeWidth={1.8} /> Connect on LinkedIn
              </a>
              <button
                type="button"
                onClick={() => onOpenChange?.(false)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 text-trital-navy text-[13px] font-semibold hover:bg-slate-50"
                data-testid="easy-apply-success-close"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 lg:p-7 space-y-4" data-testid="easy-apply-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input
                  ref={firstInput}
                  type="text"
                  value={form.name}
                  onChange={upd("name")}
                  required
                  autoComplete="name"
                  className="tc-input"
                  data-testid="easy-apply-name"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={upd("email")}
                  required
                  autoComplete="email"
                  className="tc-input"
                  data-testid="easy-apply-email"
                />
              </Field>
              <Field label="LinkedIn URL (optional)">
                <input
                  type="url"
                  value={form.linkedin}
                  onChange={upd("linkedin")}
                  placeholder="https://linkedin.com/in/…"
                  className="tc-input"
                  data-testid="easy-apply-linkedin"
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={upd("phone")}
                  placeholder="+1 …"
                  autoComplete="tel"
                  className="tc-input"
                  data-testid="easy-apply-phone"
                />
              </Field>
              <Field label="Years in this role">
                <select value={form.years} onChange={upd("years")} className="tc-input" data-testid="easy-apply-years">
                  {YEARS_OPTIONS.map((y) => <option key={y}>{y}</option>)}
                </select>
              </Field>
              <Field label="Earliest start">
                <select value={form.start} onChange={upd("start")} className="tc-input" data-testid="easy-apply-start">
                  {START_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Why TRITAL Care? (optional)">
              <textarea
                rows={3}
                value={form.why}
                onChange={(e) => setForm({ ...form, why: e.target.value.slice(0, 280) })}
                placeholder="A sentence or two — what would make this job great for you?"
                className="tc-input resize-none"
                data-testid="easy-apply-why"
              />
              <div className="mt-1 text-[10.5px] text-slate-400 text-right">{form.why.length}/280</div>
            </Field>

            {/* TCPA SMS consent — ALWAYS visible (SimpleTexting requirement) */}
            <div data-testid="easy-apply-sms-consent-wrapper">
              <SMSConsentBlock
                checked={form.smsOptIn}
                onCheckedChange={(v) => setForm({ ...form, smsOptIn: v })}
                checkboxId="easy-apply-sms-consent"
              />
            </div>

            {/* Résumé / CV upload */}
            <Field label="Résumé / CV (optional · PDF, DOC, DOCX · max 5 MB)">
              <input
                ref={fileInput}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onFileChosen}
                className="hidden"
                data-testid="easy-apply-resume-input"
              />
              {resume ? (
                <div
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60"
                  data-testid="easy-apply-resume-attached"
                >
                  <FileText className="w-4 h-4 text-emerald-700 shrink-0" strokeWidth={1.8} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[12.5px] text-slate-900 truncate">{resume.filename}</div>
                    <div className="text-[10.5px] text-slate-500">{(resume.size / 1024).toFixed(0)} KB · uploaded</div>
                  </div>
                  <button
                    type="button"
                    onClick={removeResume}
                    className="p-1 rounded-md text-slate-500 hover:text-trital-red hover:bg-white"
                    aria-label="Remove résumé"
                    data-testid="easy-apply-resume-remove"
                  >
                    <X className="w-4 h-4" strokeWidth={1.8} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={pickResume}
                  disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-700 text-[12.5px] font-semibold hover:border-trital-navy hover:text-trital-navy hover:bg-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="easy-apply-resume-pick"
                >
                  {uploading ? "Uploading…" : <><Upload className="w-4 h-4" strokeWidth={1.8} /> Attach résumé / CV</>}
                </button>
              )}
            </Field>

            <DialogFooter className="flex flex-col sm:flex-row sm:items-center gap-2.5 pt-2">
              <a
                href={linkedInCompose}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 text-slate-700 text-[13px] font-semibold hover:bg-slate-50 order-2 sm:order-1"
                data-testid="easy-apply-email-fallback"
                title="Open this application as a prefilled email draft"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={1.8} /> Send as email
              </a>
              <div className="order-1 sm:order-2 flex-1 flex flex-col sm:flex-row gap-2.5">
                <a
                  href={LINKEDIN_COMPANY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[#0A66C2] text-[#0A66C2] text-[13px] font-semibold hover:bg-[#0A66C2] hover:text-white transition-colors"
                  data-testid="easy-apply-open-linkedin"
                >
                  <Linkedin className="w-4 h-4" strokeWidth={1.8} /> Continue on LinkedIn
                </a>
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="easy-apply-submit"
                >
                  {submitting ? "Sending…" : <>Easy Apply <Send className="w-4 h-4" strokeWidth={1.8} /></>}
                </button>
              </div>
            </DialogFooter>

            <div className="text-[10.5px] text-slate-400 text-center leading-snug">
              By submitting you agree to our Privacy Notice. One response, within 5 business days. Equal Opportunity Employer.
            </div>
            <div className="text-center pt-1">
              <a
                href="sms:+18323441525"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-trital-navy hover:text-trital-red transition-colors"
                data-testid="easy-apply-text-instead"
              >
                <MessageSquare className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Quick question? Text our recruiter →
              </a>
            </div>
          </form>
        )}
      </DialogContent>

      {/* Scoped input styling so we don't leak globals */}
      <style>{`
        .tc-input {
          width: 100%;
          padding: 0.625rem 0.75rem;
          border-radius: 0.625rem;
          border: 1px solid rgb(203 213 225);
          font-size: 13px;
          line-height: 1.3;
          background: white;
          transition: border-color 120ms, box-shadow 120ms;
        }
        .tc-input:focus {
          outline: none;
          border-color: #13277A;
          box-shadow: 0 0 0 3px rgba(19, 39, 122, 0.18);
        }
      `}</style>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-600">
        {label}{required ? <span className="text-trital-red ml-0.5">*</span> : null}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
